import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import Header from '../components/Header'
import ScanForm from '../components/ScanForm'
import ScanResult from '../components/ScanResult'
import InsufficientCreditsModal from '../components/InsufficientCreditsModal'
import WelcomeModal from '../components/WelcomeModal'
import { scanTarget } from '../lib/api'
import { useAuthStore } from '../lib/auth'

function HireMailCard({ username }) {
  const [copied, setCopied] = useState(false)
  const alias = `${username}@hiremail.net`

  const copy = () => {
    navigator.clipboard.writeText(alias)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="bg-gradient-to-br from-cyan-900/20 to-[#0a0e17] border border-cyan-500/20 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-4 h-4 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-cyan-400 font-bold text-sm">Your Protected Inbox</span>
          </div>
          <p className="text-gray-500 text-xs leading-relaxed mb-3">
            Use this address on your resume and job boards. Recruiter emails are automatically scanned before they reach you.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 bg-black/30 text-cyan-300 text-sm px-3 py-2 rounded-lg font-mono border border-cyan-500/15 truncate">
              {alias}
            </code>
            <button
              onClick={copy}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all border ${
                copied
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'
                  : 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25 hover:bg-cyan-500/25'
              }`}
            >
              {copied ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const landingState = location.state || {}
  const { profile } = useAuthStore()
  const refreshProfile = useAuthStore((s) => s.refreshProfile)
  const updateCreditsFromScan = useAuthStore((s) => s.updateCreditsFromScan)
  const getSubscriptionInfo = useAuthStore((s) => s.getSubscriptionInfo)
  const subInfo = getSubscriptionInfo()

  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [successBanner, setSuccessBanner] = useState(false)
  const [creditsBanner, setCreditsBanner] = useState(false)
  const [showCreditsModal, setShowCreditsModal] = useState(false)
  const [showWelcome, setShowWelcome] = useState(false)

  // Show welcome popup for first-time users
  useEffect(() => {
    if (profile && !localStorage.getItem(`welcomed_${profile.id}`)) {
      const timer = setTimeout(() => setShowWelcome(true), 2500)
      return () => clearTimeout(timer)
    }
  }, [profile])

  const dismissWelcome = () => {
    if (profile) localStorage.setItem(`welcomed_${profile.id}`, 'true')
    setShowWelcome(false)
  }

  // Handle Stripe redirects
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccessBanner(true)
      refreshProfile()
      setSearchParams({}, { replace: true })
    }
    if (searchParams.get('credits_purchased') === 'true') {
      setCreditsBanner(true)
      refreshProfile()
      setSearchParams({}, { replace: true })
    }
    if (searchParams.get('credits_canceled') === 'true') {
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, refreshProfile, setSearchParams])

  const handleScan = async (targetType, targetValue, userContext) => {
    setLoading(true)
    setResult(null)
    setError(null)

    // Yield to React so the loading state renders before the fetch begins
    await new Promise(r => setTimeout(r, 0))

    try {
      const scanResult = await scanTarget(targetType, targetValue, userContext)
      if (!scanResult) {
        setError('System error: No result returned from scan service.')
      } else {
        setResult(scanResult)
        if (scanResult.credits) {
          updateCreditsFromScan(scanResult.credits)
        }
      }
    } catch (err) {
      if (err.insufficientCredits) {
        setShowCreditsModal(true)
      } else {
        setError(err.message || 'Scan failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const isPremiumOrTrial = subInfo.isPremium || subInfo.isTrial

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <main className="container mx-auto px-4 py-10 max-w-4xl">

        {/* Payment success banner */}
        {successBanner && (
          <div className="bg-emerald-500/15 text-emerald-400 p-4 rounded-xl mb-6 text-center border border-emerald-500/25 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Payment successful! Your subscription is now active. Welcome aboard.
          </div>
        )}

        {/* Credits purchased banner */}
        {creditsBanner && (
          <div className="bg-cyan-500/15 text-cyan-400 p-4 rounded-xl mb-6 text-center border border-cyan-500/25 flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Credits added successfully! Your balance has been updated.
          </div>
        )}

        {/* Subscription status banners */}
        {subInfo.isCancelled && (
          <div className="bg-red-500/10 border border-red-500/25 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-red-400 font-semibold">Subscription Cancelled</p>
              <p className="text-gray-400 text-sm mt-0.5">Your access has been downgraded. Re-subscribe anytime.</p>
            </div>
            <button onClick={() => navigate('/plans')} className="btn-primary text-sm flex-shrink-0">
              Re-subscribe
            </button>
          </div>
        )}

        {subInfo.isTrial && (
          <div className="bg-purple-500/10 border border-purple-500/25 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-purple-400 font-semibold">Premium Trial Active</p>
              <p className="text-gray-400 text-sm mt-0.5">
                {subInfo.daysRemaining} days remaining — all Premium features unlocked.
              </p>
            </div>
            <button onClick={() => navigate('/plans')} className="btn-primary text-sm flex-shrink-0">
              Upgrade to Keep
            </button>
          </div>
        )}

        {!subInfo.isPremium && !subInfo.isTrial && !subInfo.isCancelled && (
          <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-amber-400 font-semibold">Basic Plan</p>
              <p className="text-gray-400 text-sm mt-0.5">Upgrade to Premium for HireMail protection and advanced scanning.</p>
            </div>
            <button onClick={() => navigate('/plans')} className="btn-primary text-sm flex-shrink-0">
              Upgrade
            </button>
          </div>
        )}

        {/* Welcome header */}
        {profile && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white">
              Welcome back, {profile.first_name || profile.username || 'Member'}
            </h2>
          </div>
        )}

        <div className="space-y-5">

          {/* HireMail alias card — prominent for premium/trial members */}
          {profile?.username && isPremiumOrTrial && (
            <HireMailCard username={profile.username} />
          )}

          {/* HireMail upgrade nudge for basic members */}
          {profile?.username && !isPremiumOrTrial && (
            <div className="border border-dashed border-white/10 rounded-xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/5 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-gray-400 font-semibold text-sm">Protected Inbox Locked</p>
                  <p className="text-gray-600 text-xs mt-0.5">Upgrade to Premium to activate your HireMail auto-scan address.</p>
                </div>
              </div>
              <button onClick={() => navigate('/plans')} className="btn-secondary text-xs flex-shrink-0">
                Unlock
              </button>
            </div>
          )}

          {/* Scan form */}
          <ScanForm
            onScan={handleScan}
            loading={loading}
            initialType={landingState.type}
            initialValue={landingState.value}
          />

          {error && (
            <div className="card bg-red-500/10 border-red-500/20">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {result && <ScanResult result={result} />}

        </div>
      </main>

      {showWelcome && (
        <WelcomeModal
          onClose={dismissWelcome}
          daysRemaining={subInfo.daysRemaining || 0}
        />
      )}

      {showCreditsModal && (
        <InsufficientCreditsModal onClose={() => setShowCreditsModal(false)} />
      )}
    </div>
  )
}
